import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { history } from 'lib';
import { AuthEntity } from '_entities';
import { PERMISSIONS, URLS } from '_constants';
import HasAccess from 'modules/AccessControl/hasAccess';
import { useUnfinishedDrafts, useBodyModalOpen } from 'hooks';
import { notification } from 'utils/services';
import DraftListToast from './DraftListToast';

const { getAuthenticatedUser } = AuthEntity.selectors;

const TOAST_ID = 'survey-drafts-reminder';

// Let the app settle first: bootstrap finishes, the dashboard mounts, and the notification
// bell fires its own toast. Arriving after all that reads as a deliberate reminder rather
// than as part of page load.
const SHOW_DELAY_MS = 4000;
// notification.info() calls a blanket toast.dismiss(), and the unread-messages toast on the
// dashboard fires it, so the reminder can vanish without the user touching it. Put it back
// when that happens — dismissedRef is what keeps a real dismissal from being undone.
const HEAL_INTERVAL_MS = 20000;

// The reminder is hidden while a survey is open. Without this the undismissable toast would
// sit over the very survey the user went there to finish, and re-raise itself every 20s.
const isOnSurveyPage = (pathname) => !!pathname && pathname.startsWith(`${URLS.survey}/`);

const TOAST_OPTIONS = {
  toastId: TOAST_ID,
  closeOnClick: false,
  closeButton: false,
  draggable: false,
};

/**
 * Reminds the user about surveys they started and never submitted.
 *
 * Mounted once in App.js beside VersionWatcher — a sibling of <Router>, so it does NOT
 * remount on navigation. That is the whole reason the reminder can fire once per app load
 * without a module-level flag or a stored "already shown" marker: PageContainer and TopBar
 * remount on every route change, this does not.
 *
 * Being outside <Router> also means no useHistory/<Link> here or in the toast body — the
 * history singleton from `lib` is the supported way to navigate.
 */
function DraftReminder() {
  const isAuthenticated = useSelector(getAuthenticatedUser);
  // Every link goes to the survey route, which PrivateRoute gates on ADD_VISIT. Without it
  // each row would bounce the user to the dashboard, so show nothing at all.
  const canAddVisit = HasAccess(PERMISSIONS.ADD_VISIT);
  const enabled = !!isAuthenticated && !!canAddVisit;
  const { drafts, refresh } = useUnfinishedDrafts(enabled);

  // This component sits outside <Router> (that is what keeps it mounted across navigation),
  // so the route has to come from the history singleton rather than useLocation.
  const [pathname, setPathname] = useState(history.location.pathname);
  useEffect(() => history.listen((location) => setPathname(location.pathname)), []);

  // Closing the reminder stands it down for the current page only. Navigating anywhere else
  // clears that, and a page refresh remounts the component, so the flag starts false again.
  const dismissedRef = useRef(false);

  // A draft created during this page session only becomes visible if the list is re-read.
  // Navigating away from a half-finished survey is exactly when that matters.
  useEffect(() => {
    dismissedRef.current = false;
    refresh();
  }, [pathname, refresh]);

  // The toast container outranks the modal layer (100000 vs ui-kit/Modal's 9000), so a
  // reminder left up during a dialog paints over it — and with no close control that left
  // the user unable to reach the buttons on, say, the log-out confirmation.
  const isModalOpen = useBodyModalOpen();

  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  const suppressed = isOnSurveyPage(pathname) || isModalOpen;
  const suppressedRef = useRef(suppressed);
  suppressedRef.current = suppressed;

  const handleOpen = useCallback((url) => {
    // Hidden rather than dismissed: the suppression below keeps it away while the survey is
    // open, and it returns on its own once the user leaves without finishing.
    notification.dismiss(TOAST_ID);
    // pages/Survey treats this as in-app navigation and always shows the recovery modal.
    // Without the flag the first click after a page load restores silently, because that
    // page's `appAlreadyRunning` flag is still false.
    history.push(url, { fromDraftReminder: true });
  }, []);

  // Set only by the close button, never by onClose: onClose cannot tell a user dismissal from
  // a foreign toast.dismiss(), and conflating the two would make the heal below either useless
  // (a real dismissal is never honoured) or obnoxious (a stray dismissal is never repaired).
  const handleDismiss = useCallback(() => {
    dismissedRef.current = true;
  }, []);

  const show = useCallback(() => {
    const rows = draftsRef.current;
    if (!rows.length || suppressedRef.current || dismissedRef.current) return;
    notification.custom(
      ({ closeToast }) => (
        <DraftListToast drafts={rows} onOpen={handleOpen} onDismiss={handleDismiss} closeToast={closeToast} />
      ),
      TOAST_OPTIONS,
    );
  }, [handleOpen, handleDismiss]);

  // First appearance.
  useEffect(() => {
    if (!enabled || !drafts.length || suppressed || dismissedRef.current) return undefined;
    const timer = setTimeout(show, SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [enabled, drafts.length, suppressed, show]);

  // Nothing left to finish — take the reminder away rather than leaving a stale list up.
  useEffect(() => {
    if (enabled && drafts.length && !suppressed) return;
    notification.dismiss(TOAST_ID);
  }, [enabled, drafts.length, suppressed]);

  // Repair a toast that something else dismissed. Pure in-memory bookkeeping: no IDB read,
  // no network, just "should this be on screen, and is it?".
  useEffect(() => {
    if (!enabled) return undefined;
    const interval = setInterval(() => {
      if (suppressedRef.current || dismissedRef.current || !draftsRef.current.length) return;
      if (notification.isActive(TOAST_ID)) return;
      show();
    }, HEAL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, show]);

  // A submitted survey deletes its draft, so re-read when the user comes back to the tab.
  useEffect(() => {
    if (!enabled) return undefined;
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [enabled, refresh]);

  return null;
}

export default DraftReminder;
